import React, { useState, useEffect } from 'react';
import { annonceService } from '../../api';
import './AnnonceUser.css';
import { useNavigate, Link } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AnnonceUser = () => {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnnonces();
  }, []);

  const fetchAnnonces = async () => {
    try {
      setLoading(true);
      const response = await annonceService.getUserAnnonces();
      
      const data = Array.isArray(response) 
        ? response 
        : response?.data || [];
          
      setAnnonces(data);
    } catch (err) {
      setError(err.message || "Erreur lors du chargement des annonces");
      console.error("Erreur détaillée:", err);
      toast.error("Erreur lors du chargement des annonces");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (annonceId, annonceTitre) => {
    confirmAlert({
      title: 'Confirmer la suppression',
      message: `Êtes-vous sûr de vouloir supprimer l'annonce "${annonceTitre}" ? Cette action est irréversible.`,
      buttons: [
        {
          label: 'Oui, supprimer',
          onClick: async () => {
            try {
              setDeletingId(annonceId);
              
              toast.info('Suppression en cours...', { autoClose: false });
              
              const response = await annonceService.delete(annonceId);
              
              if (response.success) {
                setAnnonces(prevAnnonces => 
                  prevAnnonces.filter(annonce => annonce.id !== annonceId)
                );
                
                toast.success(response.message || 'Annonce supprimée avec succès !');
                
                if (annonces.length === 1) {
                  setTimeout(() => fetchAnnonces(), 1000);
                }
              } else {
                throw new Error(response.message || 'Erreur lors de la suppression');
              }
            } catch (err) {
              console.error('Erreur détaillée suppression:', err);
              
              let errorMessage = 'Erreur lors de la suppression de l\'annonce';
              
              if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
              } else if (err.message) {
                errorMessage = err.message;
              }
              
              toast.error(errorMessage);
              
              if (err.status === 401 || err.response?.status === 401) {
                setTimeout(() => {
                  navigate('/connexion');
                }, 2000);
              }
            } finally {
              setDeletingId(null);
              toast.dismiss();
            }
          }
        },
        {
          label: 'Annuler',
          onClick: () => {
            toast.info('Suppression annulée');
          }
        }
      ],
      closeOnEscape: true,
      closeOnClickOutside: true,
      willUnmount: () => toast.dismiss()
    });
  };

  const isValidAnnonce = (annonce) => {
    return annonce && 
           typeof annonce === 'object' && 
           'id' in annonce && 
           'titre' in annonce;
  };

  const getImageUrl = (annonce) => {
    if (!annonce.image) return '/placeholder-image.jpg';
    
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
    if (annonce.image.startsWith('http')) {
      return annonce.image;
    } else {
      const imagePath = annonce.image.startsWith('storage/') 
        ? annonce.image.replace('storage/', '') 
        : annonce.image;
      
      return `${baseUrl}/storage/${imagePath}`;
    }
  };

  if (loading) return (
    <div className="annonce-user-container">
      <div className="container-chargement">
        <div className="spinner-chargement"></div>
        <p>Chargement de vos annonces...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="annonce-user-container">
      <div className="container-erreur">
        <h3>Erreur</h3>
        <p className="message-erreur">{error}</p>
        <button onClick={fetchAnnonces} className="bouton-reessayer">
          Réessayer
        </button>
      </div>
    </div>
  );

  return (
    <div className="annonce-user-container">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <div className="annonce-user-header">
        <h2>Mes annonces</h2>
        <p className="annonce-count">
          {annonces.length} annonce{annonces.length > 1 ? 's' : ''} publiée{annonces.length > 1 ? 's' : ''}
        </p>
      </div>

      <div className="actions-header">
        <Link to="/ajouter-annonce" className="lien-ajout-annonce">
          <i className="fas fa-plus"></i> Créer une annonce
        </Link>
        
        <Link to="#" onClick={(e) => { e.preventDefault(); fetchAnnonces(); }} 
              className="lien-actualiser">
          <i className="fas fa-sync-alt"></i> Actualiser
        </Link>
      </div>

      {!annonces || annonces.length === 0 ? (
        <div className="container-aucune-annonce">
          <div className="contenu-aucune-annonce">
            <i className="fas fa-newspaper"></i>
            <h3>Aucune annonce publiée</h3>
            <p>Vous n'avez pas encore publié d'annonces.</p>
            <Link to="/ajout-annonce" className="lien-premiere-annonce">
              <i className="fas fa-plus"></i> Créer ma première annonce
            </Link>
          </div>
        </div>
      ) : (
        <div className="liste-annonces"> 
          {annonces
            .filter(annonce => isValidAnnonce(annonce))
            .map((annonce) => (
              <div key={annonce.id} className="carte-annonce">
                <div className="en-tete-carte">
                  <span className="statut-annonce">
                    {annonce.status === 'active' ? (
                      <span className="statut-actif">
                        <i className="fas fa-check-circle"></i> Active
                      </span>
                    ) : (
                      <span className="statut-inactif">
                        <i className="fas fa-clock"></i> En attente
                      </span>
                    )}
                  </span>
                  <span className="date-annonce">
                    <i className="far fa-calendar"></i> 
                    {new Date(annonce.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                
                {annonce.image && (
                  <div className="container-image-annonce">
                    <img 
                      src={getImageUrl(annonce)}
                      alt={annonce.titre || 'Annonce'} 
                      className="image-annonce"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder-image.jpg';
                      }}
                    />
                  </div>
                )}
                
                <div className="contenu-annonce">
                  <h3 className="titre-annonce">{annonce.titre || 'Titre non disponible'}</h3>
                  <p className="description-annonce">
                    {annonce.description 
                      ? (annonce.description.length > 150 
                        ? `${annonce.description.substring(0, 150)}...` 
                        : annonce.description)
                      : 'Aucune description fournie'
                    }
                  </p>
                  
                  <div className="meta-annonce">
                    {annonce.ville && (
                      <div className="element-meta">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{annonce.ville}</span>
                      </div>
                    )}
                    {annonce.prix && (
                      <div className="element-meta">
                        <i className="fas fa-euro-sign"></i>
                        <span>{parseFloat(annonce.prix).toFixed(2)} €</span>
                      </div>
                    )}
                    {annonce.type_annonce && (
                      <div className="element-meta">
                        <span className={`type-annonce ${annonce.type_annonce}`}>
                          {annonce.type_annonce === 'offre' ? 'Offre' : 'Demande'}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {annonce.sous_service && (
                    <div className="categorie-annonce">
                      <i className="fas fa-tag"></i>
                      <span>{annonce.sous_service.nom || 'Non catégorisé'}</span>
                    </div>
                  )}
                </div>
                
                <div className="actions-annonce">
                  <Link 
                    to={`/annonces/${annonce.id}`}
                    className="lien-action lien-voir"
                    title="Voir l'annonce"
                  >
                    <i className="fas fa-eye"></i> Voir
                  </Link>
                  
                  <Link 
                    to={`/modifier-annonce/${annonce.id}`}
                    className="lien-action lien-modifier"
                    title="Modifier l'annonce"
                  >
                    <i className="fas fa-edit"></i> Modifier
                  </Link>
                  
                  <button 
                    className="bouton-supprimer"
                    onClick={() => handleDelete(annonce.id, annonce.titre)}
                    disabled={deletingId === annonce.id}
                    title="Supprimer l'annonce"
                  >
                    <i className="fas fa-trash"></i> 
                    {deletingId === annonce.id ? 'Suppression...' : 'Supprimer'}
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default AnnonceUser;